"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';

function FormulaTerm({
    label, desc, delay, highlight,
}: { label: string; desc: string; delay: number; highlight?: boolean }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay, ease: 'easeOut' }}
            className="flex flex-col items-center text-center gap-1.5 rounded-2xl px-6 py-6 w-full max-w-[300px]"
            style={{
                background: highlight ? PURPLE : '#fbfdfd',
                border: highlight ? 'none' : '1px solid rgba(8,4,0,0.1)',
                boxShadow: highlight ? '0 20px 44px -18px rgba(55,48,163,0.5)' : '0 12px 30px -20px rgba(8,4,0,0.18)',
            }}
        >
            <span className="text-base font-bold leading-snug" style={{ fontFamily: CLASH, color: highlight ? '#ffffff' : INK }}>
                {label}
            </span>
            <span className="text-[11px]" style={{ fontFamily: MONO, color: highlight ? 'rgba(255,255,255,0.65)' : 'rgba(8,4,0,0.45)' }}>
                {desc}
            </span>
        </motion.div>
    );
}

function FormulaSymbol({ children, delay }: { children: React.ReactNode; delay: number }) {
    return (
        <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay, ease: 'backOut' }}
            className="shrink-0 text-2xl font-bold"
            style={{ fontFamily: CLASH, color: PURPLE }}
        >
            {children}
        </motion.span>
    );
}

function AnalyticsFormula() {
    return (
        <div className="relative w-full max-w-[300px] mx-auto flex flex-col items-center justify-center gap-3">
            <FormulaTerm label="Marketing Analytics" desc="collect + interpret" delay={0.1} />
            <FormulaSymbol delay={0.35}>+</FormulaSymbol>
            <FormulaTerm label="Attribution" desc="weigh each touchpoint" delay={0.2} />
            <FormulaSymbol delay={0.5}>=</FormulaSymbol>
            <FormulaTerm label="Clearer Growth" desc="what's working, where next" delay={0.3} highlight />
        </div>
    );
}

export default function AnalyticsWhatIs() {
    return (
        <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <div className="lg:col-span-7 flex flex-col gap-6">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        The Basics
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What Is Marketing Analytics &amp; Attribution?
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex flex-col gap-5"
                    >
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}>
                            Marketing analytics is the process of collecting and interpreting marketing data to understand performance and customer behaviour.
                        </p>
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}>
                            Attribution adds another layer by helping businesses understand how different marketing touchpoints contribute to a conversion.
                        </p>
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}>
                            Together, they provide a clearer view of what is working, where customers are coming from and where marketing investment can be improved.
                        </p>
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <AnalyticsFormula />
                </div>
            </div>
        </section>
    );
}
